// Shared HTTP helper utilities (Phase 1)
import type { AxiosRequestConfig } from 'axios';
import type { components } from '@/core/types/generated/api';

export type ProblemDetails = components['schemas']['ProblemDetails'];
export type ValidationErrorResponse = components['schemas']['ValidationErrorResponse'];
export type ApiErrorResponse = components['schemas']['ApiErrorResponse'];

export const PAYLOAD_TOO_LARGE_STATUS = 413;

export interface VersionedRequestOptions {
  version?: string;
}

export interface PaginatedResult<T> {
  items: T[];
  meta?: components['schemas']['PaginationMetadata'];
  links?: components['schemas']['PaginationLinks'];
}

// Type guards for different error response types
export function isProblemDetails(obj: any): obj is ProblemDetails {
  return obj && typeof obj === 'object' && 'title' in obj && 'status' in obj && !('errors' in obj) && !('traceId' in obj);
}

export function isValidationErrorResponse(obj: any): obj is ValidationErrorResponse {
  return obj && typeof obj === 'object' && 'title' in obj && 'status' in obj && 'errors' in obj && typeof obj.errors === 'object';
}

export function isApiErrorResponse(obj: any): obj is ApiErrorResponse {
  return obj && typeof obj === 'object' && 'title' in obj && 'status' in obj && 'type' in obj && ('traceId' in obj || 'detail' in obj || 'instance' in obj);
}

export function isPayloadTooLargeError(error: unknown): error is ApiErrorResponse & { status: typeof PAYLOAD_TOO_LARGE_STATUS } {
  if (!isApiErrorResponse(error)) {
    return false;
  }

  if (error.status !== PAYLOAD_TOO_LARGE_STATUS) {
    return false;
  }

  const type = typeof error.type === 'string' ? error.type.toLowerCase() : '';
  const typeHintsPayloadTooLarge = type.includes('payload') && type.includes('large');

  return type === '' ? true : typeHintsPayloadTooLarge;
}

// Utility for adding version headers to requests
export interface ApiRequestConfig {
  version?: string;
  headers?: Record<string, string>;
}

export function addVersionHeaders(config: ApiRequestConfig = {}): Record<string, string> {
  const headers = { ...config.headers };
  
  if (config.version) {
    headers['x-API-Version'] = config.version;
  }
  
  return headers;
}

export function buildVersionedRequestConfig(options: VersionedRequestOptions = {}): AxiosRequestConfig | undefined {
  if (!options.version) {
    return undefined;
  }

  return {
    params: { version: options.version },
    headers: addVersionHeaders({ version: options.version })
  };
}

export function buildRequestConfig(
  params?: Record<string, unknown>,
  options?: VersionedRequestOptions
): AxiosRequestConfig | undefined {
  const versionedConfig = buildVersionedRequestConfig(options);

  if (!params) {
    return versionedConfig;
  }

  return {
    ...(versionedConfig ?? {}),
    params: {
      ...(versionedConfig?.params ?? {}),
      ...params
    }
  };
}

// Utility for building query parameters with version support
export function buildQueryParams(params: Record<string, any> = {}, options: { version?: string } = {}): Record<string, any> {
  const queryParams: Record<string, any> = {};
  
  // Add version to query params if specified
  if (options.version) {
    queryParams.version = options.version;
  }
  
  // Add other parameters, filtering out undefined/null/empty values
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      queryParams[key] = value;
    }
  }
  
  return queryParams;
}

export function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  return promise.then(r => (r as any).data as T);
}
